import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import PaginationRequest from '@/core/domain/pagination/PaginationRequest.ts'
import PaginationResponse from '@/core/domain/pagination/PaginationResponse.ts'
import { type UserInput } from '@/core/domain/users/UserInput.ts'
import {
  LocalUserFactory
} from '@/core/infrastructure/mappers/users/LocalUserFactory.ts'
import {
  type PaginationResponseEntity,
  PaginationResponseSchema,
  type PaginationResponseUserEntity
} from '@/core/infrastructure/schemas/pagination/PaginationSchema.ts'
import type IUsersRepository from '@/core/domain/users/IUsersRepository.ts'
import * as qs from 'qs'
import { LocalUser } from '@/core/domain/users/LocalUser.ts'
import type { UserResetPasswordEntity } from '@/core/infrastructure/schemas/users/core/UserInputSchema.ts'
import { resolveView } from '@/core/infrastructure/services/ViewResolver.ts'
import UserSearchRequest from '@/core/domain/users/UserSearchRequest.ts'
import type { LocalUserEntity } from '@/core/infrastructure/schemas/users/core/LocalUserSchema.ts'

export class UsersRepository implements IUsersRepository {
  /**
   * Fetch user by identifier
   * @param identifier
   * @param refresh
   */
  async fetchByIdentifier(identifier: string, refresh: boolean = false): Promise<LocalUser> {
    const response = await apiService.get<LocalUserEntity>(`admin/users/${identifier}`, {
      params: { refresh }
    })
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Fetch all users
   * @param filters
   * @param request
   */
  async fetchAll(filters: UserSearchRequest, request: PaginationRequest): Promise<PaginationResponse<LocalUser>> {
    const response = await apiService.get<PaginationResponseUserEntity>('admin/users', {
      params: {...filters.toQueryParams(), ...request.toQueryParams() },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
    })

    return PaginationResponse.create<LocalUser>(
      response.data,
      PaginationResponseSchema,
      (data: PaginationResponseEntity) => {
        data.content = data.content.map((user: any) =>
          LocalUserFactory.createLocalUser(user, resolveView(response.getHeader('accept-view')))
        )
        return new PaginationResponse<LocalUser>(data)
      }
    )
  }

  /**
   * Create a new user
   * @param input
   */
  async create(input: UserInput): Promise<LocalUser> {
    const response = await apiService.post<LocalUserEntity>('admin/users', input)
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Update an existing user
   * @param input
   */
  async update(input: UserInput): Promise<LocalUser> {
    const response = await apiService.put<LocalUserEntity>(`admin/users/${input.identifier}`, input)
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Verify an email address for a user
   * @param identifier
   */
  async verifyEmail(identifier: string): Promise<LocalUser> {
    const response = await apiService.put<LocalUserEntity>(`admin/users/${identifier}/verify-email`, {})
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Block a user
   * @param identifier
   */
  async block(identifier: string): Promise<LocalUser> {
    const response = await apiService.put<LocalUserEntity>(`admin/users/${identifier}/block`, {})
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Unblock a user
   * @param identifier
   */
  async unblock(identifier: string): Promise<LocalUser> {
    const response = await apiService.put<LocalUserEntity>(`admin/users/${identifier}/unblock`, {})
    return LocalUserFactory.createLocalUser(response.data, resolveView(response.getHeader('accept-view')))
  }

  /**
   * Reset a user password
   * @param identifier
   */
  async resetPassword(identifier: string): Promise<string> {
    const response = await apiService.put<UserResetPasswordEntity>(
      `admin/users/${identifier}/reset-password`,
      {}
    )
    return response.data.password
  }

  async sendEmailResetPassword(identifier: string): Promise<boolean> {
    const response = await apiService.post<UserResetPasswordEntity>(
      `admin/users/${identifier}/reset-password/email`,
      {}
    )
    return response.status === 202
  }
}
